import {
  GlassCard,
  SolidBlock,
  HighlightBanner,
  BrutalistBadge,
} from "@/app/components/brand-scaffold";

export function PlaygroundCompositionSection() {
  return (
    <div>
      <div className="border-l-8 border-black pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION D</p>
        <h2 className="text-3xl font-black uppercase">拼接与叠放规范</h2>
      </div>
      <p className="font-bold text-zinc-500 mb-10 text-sm">所有组件均如同物理积木。本区规定了它们之间的间距（Gap/Margin）、包裹关系和层叠秩序。</p>

      <div className="space-y-16">
        {/* D-1: GlassCard 系 */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【D.1】GlassCard (C.1) 的拼合</h3>
          <div className="space-y-12">
            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.1.A】有间并列</h4>
              <p className="text-sm font-bold text-zinc-700 mb-6">并排间距使用 <code className="bg-black text-[#A3E635] px-1 py-0.5 mx-1">gap-8</code>。玻璃卡绝不能互相黏连，必须给右侧和下方 6px 实体阴影留出呼吸空间。</p>
              <div className="grid lg:grid-cols-2 gap-8">
                <GlassCard className="h-32 flex items-center justify-center bg-white border-dashed"><span className="font-black text-2xl opacity-20">有间卡 A</span></GlassCard>
                <GlassCard className="h-32 flex items-center justify-center bg-white border-dashed"><span className="font-black text-2xl opacity-20">有间卡 B</span></GlassCard>
              </div>
            </div>

            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.1.B】无间并列（边框融合）</h4>
              <p className="text-sm text-zinc-600 mb-6">使用 <code className="bg-black text-[#A3E635] px-1 mx-1">-ml-1</code> 吞掉相邻边框，外层父包裹统一阴影。多卡强制拼为一块连续矩阵面。</p>
              <div className="flex flex-col md:flex-row shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <GlassCard className="w-full h-32 flex items-center justify-center bg-white z-10 relative !shadow-none"><span className="font-black text-2xl opacity-20">无间连版 A</span></GlassCard>
                <GlassCard className="w-full h-32 flex items-center justify-center bg-white border-t-0 md:border-t-4 md:border-l-0 -mt-1 md:mt-0 md:-ml-1 z-0 relative !shadow-none"><span className="font-black text-2xl opacity-20">无间连版 B</span></GlassCard>
              </div>
            </div>

            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.1.C】角标挂载（Badge 咬破 GlassCard）</h4>
              <p className="text-sm font-bold text-zinc-700 mb-6">小徽章(B.1)作为外附装饰时，必须用负边距<code className="bg-black text-[#A3E635] px-1 py-0.5 mx-1">absolute -top-5 -right-5</code>出界覆盖，撕裂直角边框的严肃感。</p>
              <div className="pt-8">
                <GlassCard className="relative max-w-xl mx-auto h-48 flex items-center justify-center bg-white">
                  <BrutalistBadge bgClass="bg-[#A3E635]" className="absolute -top-5 -right-5 z-10 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                    LATEST V2.0
                  </BrutalistBadge>
                  <BrutalistBadge bgClass="bg-[#8B5CF6]" hoverTextClass="text-white" className="absolute -left-6 top-10 z-10 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] -rotate-6">
                    POPUP
                  </BrutalistBadge>
                  <span className="font-black text-2xl opacity-20">GlassCard Base</span>
                </GlassCard>
              </div>
            </div>
          </div>
        </div>

        {/* D-2: SolidBlock 系 */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【D.2】SolidBlock (C.2) 的拼合</h3>
          <div className="space-y-12">
            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.2.A】有间并列</h4>
              <div className="grid lg:grid-cols-2 gap-8 mt-6">
                <SolidBlock className="h-32 flex items-center justify-center"><span className="font-black text-[#A3E635] text-2xl opacity-50">实底座 A</span></SolidBlock>
                <SolidBlock className="h-32 flex items-center justify-center"><span className="font-black text-[#A3E635] text-2xl opacity-50">实底座 B</span></SolidBlock>
              </div>
            </div>

            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.2.B】无间并列（仪表盘融合）</h4>
              <p className="text-sm text-zinc-600 mb-6">剥离内部各块阴影，在外层父容器统一施加巨型阴影，内部边框重叠，形成不可分割的热键分区面板。</p>
              <div className="flex flex-col lg:flex-row shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <SolidBlock bgClass="bg-black" className="w-full h-32 flex flex-col items-center justify-center relative z-10 !shadow-none !p-0 !md:p-0">
                  <span className="font-black text-[#A3E635] text-2xl opacity-50">面板 LEFT</span>
                </SolidBlock>
                <SolidBlock bgClass="bg-black" className="w-full h-32 flex items-center justify-center border-t-0 lg:border-t-4 lg:border-l-0 -mt-1 lg:mt-0 lg:-ml-1 relative z-0 !shadow-none !p-0 !md:p-0">
                  <span className="font-black text-[#A3E635] text-2xl opacity-50">面板 RIGHT</span>
                </SolidBlock>
              </div>
            </div>

            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.2.C】包裹 GlassCard（C.2 包裹 C.1）</h4>
              <p className="text-sm font-bold text-zinc-700 mb-6">SolidBlock 作为底板时，给内部预留宽阔的 <code className="bg-black text-[#A3E635] px-1 py-0.5 mx-1">p-8~p-12</code> 边距，内部子元素之间使用聚合的 <code className="bg-black text-[#A3E635] px-1 py-0.5 mx-1">gap-6</code>。</p>
              <SolidBlock className="p-8 md:p-12">
                <h5 className="text-white font-black text-xl mb-6">SOLIDBLOCK CONTAINER BASE</h5>
                <div className="grid sm:grid-cols-2 gap-6">
                  <GlassCard bgClass="bg-white text-black" className="shadow-[4px_4px_0px_0px_rgba(163,230,53,1)] border-white"><p className="font-bold">被强包裹的卡块</p></GlassCard>
                  <GlassCard bgClass="bg-white text-black" className="shadow-[4px_4px_0px_0px_rgba(163,230,53,1)] border-white"><p className="font-bold">被强包裹的卡块</p></GlassCard>
                </div>
              </SolidBlock>
            </div>

            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.2.D】异色嵌套（C.2 包裹 C.2）</h4>
              <p className="text-sm font-bold text-zinc-700 mb-6">6 包裹 6 时，内外层必须使用<strong>不同的品牌色词</strong>。防止同色喁郣导致层层之间边界消失。</p>
              <SolidBlock bgClass="bg-[#6366F1] text-white" className="p-8 md:p-12">
                <p className="text-xs font-black uppercase tracking-widest text-white/60 mb-6">OUTER LAYER — INDIGO 外壳渲染层</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <SolidBlock bgClass="bg-[#A3E635] text-black" className="p-6 md:p-8">
                    <p className="text-xs font-black uppercase tracking-widest text-black/60 mb-4">INNER CORE — GREEN 内机核心</p>
                    <p className="font-black text-xl uppercase">Core A</p>
                    <p className="text-sm font-bold text-black/70 mt-2">内部块用强对比的品牌绿，实际功能内核。</p>
                  </SolidBlock>
                  <SolidBlock bgClass="bg-[#F97316] text-white" className="p-6 md:p-8">
                    <p className="text-xs font-black uppercase tracking-widest text-white/60 mb-4">INNER CORE — ORANGE 内机状态</p>
                    <p className="font-black text-xl uppercase">Core B</p>
                    <p className="text-sm font-bold text-white/80 mt-2">第二个功能区块可用另一品牌色强化语义区分。</p>
                  </SolidBlock>
                </div>
              </SolidBlock>
            </div>
          </div>
        </div>

        {/* D-3: 跨组件 */}
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【D.3】跨组件吸附关系</h3>
          <div className="space-y-12">
            <div className="border-4 border-dashed border-black/20 p-8 bg-zinc-50">
              <h4 className="text-xl font-black uppercase mb-2">【D.3.A】Banner 吸附于主块正下方（C.3 依附 C.2）</h4>
              <p className="text-sm font-bold text-zinc-700 mb-6">HighlightBanner 常作为延伸说明，放弃大间距，以 <code className="bg-black text-[#A3E635] px-1 py-0.5 mx-1">mt-2</code> 紧贴上方主黑块落下的 8px 阴影缝隙，形成"便利贴"式的从属感。</p>
              <div className="max-w-2xl mx-auto">
                <SolidBlock className="h-40 flex items-center justify-center"><span className="font-black text-[#A3E635] text-2xl opacity-50">主级操作黑块 (下方 8px 阴影)</span></SolidBlock>
                <HighlightBanner badgeText="特例注释" badgeColor="bg-black" className="mt-2 text-sm z-10 relative">
                  这里是一条如影随形的辅助解释横幅（mt-2），它紧紧压迫在上方黑底块落下的 8px 阴影的下方缝隙边。
                </HighlightBanner>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
